import { useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import { IconButton, Tooltip } from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import React from 'react';
import { JobStatus } from 'shared/models/job';
import { getRevivalReasonLabel } from 'shared/models/scheduled-outreach';
import { PageDialogLink } from '../common/page-dialog-link';
import { timeFrom } from '../common/timestamp';

import {
    OUTREACH_SEARCH_RESULT_COUNTS,
    OutreachSearchResultCount,
    REFRESH_READY_CANDIDATES_CACHE,
    REFRESH_REVIVAL_CANDIDATES_CACHE,
    REVIVAL_OUTREACH_SEARCH_RESULT_COUNTS,
    RevivalOutreachSearchResultCount
} from '../graphql/queries/outreach';
import { useModal } from '../hooks/use-modal';
import { useSession } from '../hooks/use-session';
import { useSnackbar } from '../hooks/use-snackbar';
import { CellValue, DataPanel, DataPanelColumn } from './data-panel';
import { JobOutreachScheduleNowButton } from './job-outreach-schedule-now-button';
import { JobSearches } from './job-searches';

const styles = css`
    .MuiTableBody-root .MuiTableRow-root {
        .available-counts {
            display: flex;
            align-items: center;
            gap: 8px;
        }

        .actions-cell {
            display: flex;
            align-items: center;
            gap: 4px;
        }

        .show-on-hover {
            opacity: 0;
            transition: opacity 0.2s ease-in-out;
        }

        &:hover {
            .show-on-hover {
                opacity: 1;
            }
        }
    }
`;

type Column =
    | 'recruiter'
    | 'client'
    | 'job'
    | 'availableCount'
    | 'schedule'
    | 'revivalReason'
    | 'scheduled'
    | 'sent'
    | 'actions';

const columns: Array<DataPanelColumn<Column>> = [
    { id: 'client', label: 'Client' },
    { id: 'job', label: 'Job' },
    { id: 'recruiter', label: 'Recruiter' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'availableCount', label: 'Available Count' },
    { id: 'scheduled', label: 'Scheduled' },
    { id: 'sent', label: 'Sent' },
    { id: 'revivalReason', label: 'Revival Reason' },
    { id: 'actions', label: '' }
];

export const OutreachSearchResultCounts: React.FC<{ username?: string; revival?: boolean }> = ({
    username,
    revival
}) => {
    const { user } = useSession();
    const { getConfirmation } = useModal();
    const { setSnackbar } = useSnackbar();

    const formattedUsername = username === 'me' ? user.username : username;
    const jobStatusFilter = { job: { status: { _eq: JobStatus.Active } } };
    const userNameFilter = username ? { user: { username: { _eq: formattedUsername } } } : {};
    const { data, refetch } = useQuery<
        { searchResultCounts: OutreachSearchResultCount[] },
        { where: { user?: { username: { _eq: string } } }; job?: { status: { _eq: JobStatus } } }
    >(OUTREACH_SEARCH_RESULT_COUNTS, {
        skip: revival,
        variables: { where: { ...userNameFilter, ...jobStatusFilter } }
    });
    const { data: revivalData, refetch: revivalRefetch } = useQuery<
        { searchResultCounts: RevivalOutreachSearchResultCount[] },
        { where: { job?: { status: { _eq: JobStatus } } } }
    >(REVIVAL_OUTREACH_SEARCH_RESULT_COUNTS, {
        skip: !revival,
        variables: { where: { ...jobStatusFilter } }
    });

    const [refreshReadyCandidates, { loading: refreshReadyCandidatesLoading }] = useMutation<
        {},
        { jobId: string; userId: string; minAgeMs?: number }
    >(REFRESH_READY_CANDIDATES_CACHE);
    const [refreshRevivalCandidates, { loading: refreshRevivalCandidatesLoading }] = useMutation<
        {},
        { jobId: string; revivalReason: string; minAgeMs?: number }
    >(REFRESH_REVIVAL_CANDIDATES_CACHE);

    const handleRefreshCounts = (record: OutreachSearchResultCount | RevivalOutreachSearchResultCount) => () => {
        getConfirmation(
            async () => {
                setSnackbar('Refreshing counts...');
                if (revival) {
                    const revivalRecord = record as RevivalOutreachSearchResultCount;
                    await refreshRevivalCandidates({
                        variables: { jobId: revivalRecord.job.id, revivalReason: revivalRecord.revivalReason }
                    });
                    revivalRefetch();
                } else {
                    const readyRecord = record as OutreachSearchResultCount;
                    const targetUserId = readyRecord.user?.id ?? user.id;
                    await refreshReadyCandidates({ variables: { jobId: readyRecord.job.id, userId: targetUserId } });
                    refetch();
                }
                setSnackbar('Counts refreshed');
            },
            'This will re-calculate the outreach counts, can take a few minutes. Are you sure you want to continue?',
            'Refresh Counts'
        );
    };

    const loading = refreshReadyCandidatesLoading || refreshRevivalCandidatesLoading;

    const displayedColumns = revival
        ? columns.filter((c) => c.id !== 'recruiter' && c.id !== 'schedule' && c.id !== 'scheduled' && c.id !== 'sent')
        : username
          ? columns.filter((c) => c.id !== 'recruiter' && c.id !== 'revivalReason')
          : columns.filter((c) => c.id !== 'revivalReason');

    const getCellValue = (column: Column) => (record: OutreachSearchResultCount | RevivalOutreachSearchResultCount) => {
        switch (column) {
            case 'client':
                return record.job.client.name;
            case 'job':
                return record.job.title;
            case 'recruiter':
                return record.user?.name;
            case 'availableCount':
                return record.availableCount;
            case 'schedule':
                return null;
            case 'revivalReason':
                return revival ? getRevivalReasonLabel(record.revivalReason) : null;
            case 'scheduled':
                return record.stats?.[0]?.currentlyScheduled;
            case 'sent':
                return record.stats?.[0]?.sentInPeriod;
            case 'actions':
                return null;
            default:
                return null;
        }
    };

    const getCellRenderer = {
        actions: (_1: CellValue, record: OutreachSearchResultCount | RevivalOutreachSearchResultCount) => {
            if (revival || !record.user) {
                return null;
            }

            const readyRecord = record as OutreachSearchResultCount;

            const canSendOutreach = readyRecord.user.id === user.id;
            const sendOutreachButton = canSendOutreach ? (
                <span className="show-on-hover">
                    <JobOutreachScheduleNowButton jobId={readyRecord.job.id} jobStatus={readyRecord.job.status} />
                </span>
            ) : null;

            return <div className="actions-cell">{sendOutreachButton}</div>;
        },
        availableCount: (value: CellValue, record: OutreachSearchResultCount | RevivalOutreachSearchResultCount) => {
            const tooltip =
                loading || value === undefined
                    ? 'Loading counts'
                    : record.refreshedAt
                      ? `Counts checked ${timeFrom(record.refreshedAt)}, click to refresh`
                      : 'Counts not available, click to refresh';

            return (
                <div className="available-counts">
                    <div>{value}</div>
                    <div>
                        <Tooltip title={tooltip}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleRefreshCounts(record)}
                                    disabled={loading}
                                    className="show-on-hover"
                                >
                                    <Refresh fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </div>
                </div>
            );
        },
        job: (_1: CellValue, row: OutreachSearchResultCount) => {
            return (
                <PageDialogLink
                    url={`/job/${row.job.id}/board`}
                    Component={JobSearches}
                    componentProps={{ id: row.job.id }}
                >
                    <span>{row.job.title}</span>
                </PageDialogLink>
            );
        },
        schedule: (_1: CellValue, row: OutreachSearchResultCount) => {
            const period = row.schedule?.frequencyDays === 1 ? 'day' : `${row.schedule?.frequencyDays} days`;
            return row.schedule ? (
                <div className={`${row.schedule.isActive ? 'active' : 'inactive'}`}>
                    {row.schedule.count}/{period}
                </div>
            ) : null;
        },
        scheduled: (_1: CellValue, row: OutreachSearchResultCount) => {
            return (
                <Tooltip title="Currently scheduled">
                    <span>{row.stats?.[0]?.currentlyScheduled}</span>
                </Tooltip>
            );
        },
        sent: (_1: CellValue, row: OutreachSearchResultCount) => {
            return (
                <Tooltip title={`Sent in ${row.stats?.[0]?.frequencyDays} days`}>
                    <span>{row.stats?.[0]?.sentInPeriod}</span>
                </Tooltip>
            );
        }
    };

    return revival ? (
        <DataPanel
            columns={displayedColumns}
            data={revivalData?.searchResultCounts}
            getCellValue={getCellValue}
            filterableColumns={['client', 'job', 'recruiter', 'revivalReason']}
            pageSize={100}
            getCellRenderer={getCellRenderer}
            sortableColumns={['client', 'job', 'recruiter', 'availableCount', 'revivalReason']}
            initialSortColumn="client"
            initialSortDirection="asc"
            css={styles}
        />
    ) : (
        <DataPanel
            columns={displayedColumns}
            data={data?.searchResultCounts}
            getCellValue={getCellValue}
            filterableColumns={['client', 'job', 'recruiter']}
            pageSize={100}
            getCellRenderer={getCellRenderer}
            sortableColumns={['client', 'job', 'recruiter', 'availableCount']}
            initialSortColumn="client"
            initialSortDirection="asc"
            css={styles}
        />
    );
};
